import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Form, Button, Spinner, Dropdown } from 'react-bootstrap';
import { Stars, Upload, StopFill, ChevronRight, ChevronDown, ChevronUp, PlusCircle, InfoCircle } from 'react-bootstrap-icons';
import { useChat, ArtifactLimitStatus } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useUsage } from '../contexts/UsageContext';
import { PLAN_LIMITS } from '../types/usage.types';
import { useContextEngine } from '../contexts/ContextEngineContext';
import MarkdownRenderer from '../components/MarkdownRenderer';
import MessageActions from '../components/MessageActions';
import Footer from '../components/Footer';
import SvgCanvas from '../components/SvgCanvas';
import FileCard from '../components/FileCard';
import AttachmentDisplay from '../components/AttachmentDisplay';
import ProjectLimitModal from '../components/common/ProjectLimitModal';
import ConversationLimitModal from '../components/common/ConversationLimitModal';
import ArtifactLimitModal from '../components/common/ArtifactLimitModal';
import AlertBanner from '../components/AlertBanner';
import { extractSvgFromText, replaceSvgWithArtifactMessage, extractSvgArtifactsFromConversation, SvgArtifact } from '../utils/svgDetector';
import { filesToAttachments } from '../utils/fileUtils';
import chatService from '../services/chatService';
import './Chat.css';

// Cancel any ongoing speech synthesis immediately when this module loads
if (typeof window !== 'undefined' && window.speechSynthesis && window.speechSynthesis.speaking) {
  window.speechSynthesis.cancel();
}

const Chat: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { projects, currentProject, openNewProjectDialog, attachConversationToProject, detachConversationFromProject } = useProjects();
  const { setIsCollapsed: setSidebarCollapsed, isRightSidebarCollapsed } = useSidebar();
  const { usageStats, canSendMessage, canCreateArtifact, getMessageLimitMessage, getArtifactLimitMessage, refreshUsage } = useUsage();
  const { refreshReadiness, refreshSignals, refreshSources, refreshSuggestions, registerSignalClickHandler } = useContextEngine();
  const {
    currentConversation,
    loading,
    error,
    streamingMessageId,
    sendMessageStream,
    loadConversation,
    createConversationStream,
    cancelStreaming
  } = useChat();
  
  // Right sidebar offset: 280px expanded, 48px collapsed, 0 when not visible
  const rightSidebarOffset = currentProject
    ? (isRightSidebarCollapsed ? 48 : 280)
    : 0;

  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templateContext, setTemplateContext] = useState<{
    templateTitle?: string;
    helperPrompt?: string;
    initialPrompt?: string;
  } | null>(null);
  const [pendingUserPrompt, setPendingUserPrompt] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [hasProcessedCreation, setHasProcessedCreation] = useState(false);
  const [currentSvg, setCurrentSvg] = useState<string | null>(null);
  const [showSvgCanvas, setShowSvgCanvas] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(45);
  const [svgArtifacts, setSvgArtifacts] = useState<SvgArtifact[]>([]);
  const [selectedArtifactIndex, setSelectedArtifactIndex] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isSvgStreaming, setIsSvgStreaming] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [showProjectLimitModal, setShowProjectLimitModal] = useState(false);
  const [showConversationLimitModal, setShowConversationLimitModal] = useState(false);
  const [showArtifactLimitModal, setShowArtifactLimitModal] = useState(false);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [artifactLimitWarning, setArtifactLimitWarning] = useState<string | null>(null);
  const [ratedMessages, setRatedMessages] = useState<Set<string>>(new Set());
  const [collapsedMessages, setCollapsedMessages] = useState<Set<string>>(new Set());
  const [autoCollapsedMessages, setAutoCollapsedMessages] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isCreatingConversation = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Register signal chip click handler to insert label into chat input
  useEffect(() => {
    registerSignalClickHandler((signal) => {
      setMessage((prev) => (prev ? `${prev} ${signal.label}` : signal.label));
      textareaRef.current?.focus();
    });
    return () => registerSignalClickHandler(null);
  }, [registerSignalClickHandler]);

  // Helper functions for message collapse feature
  const MAX_COLLAPSED_LINES = 4;

  const getMessageLineCount = (content: string): number => {
    return content.split('\n').length;
  };

  const isLongMessage = (content: string): boolean => {
    return getMessageLineCount(content) > MAX_COLLAPSED_LINES;
  };

  const getTruncatedContent = (content: string): string => {
    const lines = content.split('\n');
    return lines.slice(0, MAX_COLLAPSED_LINES).join('\n') + '...';
  };

  // Stop any ongoing speech synthesis on initial mount (page refresh/load)
  useEffect(() => {
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    // Add listener to stop speech synthesis before page unload/refresh
    const handleBeforeUnload = () => {
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Show error banner when error changes
  useEffect(() => {
    if (error) {
      setShowErrorBanner(true);
    }
  }, [error]);

  // Check artifact limit and show modal if reached
  useEffect(() => {
    // Only show modal when usage stats are loaded and artifact limit is reached
    if (usageStats) {
      const { current, limit } = usageStats.usage.artifacts;
      if (limit !== null && current >= limit) {
        setArtifactLimitWarning('reached');
        setShowArtifactLimitModal(true);
      } else {
        setArtifactLimitWarning(null);
      }
    }
  }, [usageStats]);

  // Reset SVG-related state and template context when conversationId changes
  useEffect(() => {
    setCurrentSvg(null);
    setShowSvgCanvas(false);
    setSvgArtifacts([]);
    setSelectedArtifactIndex(0);
    setIsSvgStreaming(false);
    // Clear template context when switching conversations
    // It will be restored if the new conversation has template metadata
    setTemplateContext(null);
  }, [conversationId]);

  // Stop text-to-speech when navigating away or conversation changes
  useEffect(() => {
    return () => {
      // Cleanup: stop any ongoing speech synthesis
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
        setPlayingMessageId(null);
      }
    };
  }, [conversationId]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    // Check for navigation state
    const state = location.state as {
      userPrompt?: string;
      templateId?: string;
      isCreating?: boolean;
      templateTitle?: string;
      helperPrompt?: string;
      initialPrompt?: string;
      uploadedFiles?: File[];
      projectId?: string;
    } | null;

    if (conversationId?.startsWith('temp_') && state?.isCreating && !hasProcessedCreation) {
      setHasProcessedCreation(true);

      if (state.helperPrompt) {
        setTemplateContext({
          templateTitle: state.templateTitle,
          helperPrompt: state.helperPrompt,
          initialPrompt: state.initialPrompt
        });
      }

      setPendingUserPrompt(state.userPrompt || '');
      setIsInitializing(true);

      // Set uploaded files if present
      if (state.uploadedFiles && state.uploadedFiles.length > 0) {
        setUploadedFiles(state.uploadedFiles);
      }

      window.history.replaceState({}, document.title);

      // Convert files to attachments if present and create conversation
      (async () => {
        // Prevent double execution in React Strict Mode
        if (isCreatingConversation.current) {
          return;
        }

        isCreatingConversation.current = true;

        try {
          let attachments = undefined;
          if (state.uploadedFiles && state.uploadedFiles.length > 0) {
            attachments = await filesToAttachments(state.uploadedFiles);
          }

          // Prepare templateMetadata if we have a template context
          const templateMetadata = state.helperPrompt ? {
            templateTitle: state.templateTitle,
            helperPrompt: state.helperPrompt,
            initialPrompt: state.initialPrompt
          } : undefined;

          // Create AbortController for conversation creation
          if (abortControllerRef.current) {
            abortControllerRef.current = null;
          }
          abortControllerRef.current = new AbortController();

          const newConversationId = await createConversationStream(
            state.userPrompt || '',
            state.templateId,
            attachments,
            templateMetadata,
            state.projectId,
            abortControllerRef.current.signal,
            handleArtifactLimitStatus
          );
          abortControllerRef.current = null;
          // Refresh usage stats after successful conversation creation
          await refreshUsage();
          // Delayed refresh: backend signal extraction and source processing are fire-and-forget
          // and call Claude, which can take varying amounts of time. Two passes to catch fast and slow extractions.
          setTimeout(() => {
            refreshSignals();
            refreshSources();
            refreshReadiness();
            refreshSuggestions();
          }, 5000);
          setTimeout(() => {
            refreshSignals();
            refreshSources();
            refreshReadiness();
            refreshSuggestions();
          }, 15000);
          // Use navigate to properly update the route params
          navigate(`/chat/${newConversationId}`, { replace: true });
          setIsInitializing(false);
          setPendingUserPrompt(null);
          setUploadedFiles([]);
          isCreatingConversation.current = false;
        } catch (err: any) {
          console.error('Failed to create conversation:', err);
          console.error('Error details:', {
            message: err instanceof Error ? err.message : 'Unknown error',
            code: err?.code,
            stack: err instanceof Error ? err.stack : undefined
          });
          abortControllerRef.current = null;
          setIsInitializing(false);
          setPendingUserPrompt(null);
          isCreatingConversation.current = false;

          // Check if this was an abort error
          const isAbortError = (err instanceof DOMException && err.name === 'AbortError') ||
                               (err instanceof Error && err.name === 'AbortError');

          // Check for specific limit errors and grace period errors
          const isMessageLimitError = err?.code === 'MESSAGE_LIMIT_REACHED';
          const isArtifactLimitError = err?.code === 'ARTIFACT_LIMIT_REACHED';
          const isGracePeriodError = err?.code === 'GRACE_PERIOD_ACTIVE';

          if (isMessageLimitError) {
            // Show conversation limit modal - stay on page, don't navigate away (clear any existing banner)
            setLimitError(null);
            setShowConversationLimitModal(true);
          } else if (isArtifactLimitError) {
            // Show artifact limit modal - stay on page, don't navigate away
            setArtifactLimitWarning('reached');
            setShowArtifactLimitModal(true);
          } else if (isGracePeriodError) {
            // Show grace period error - redirect to select-plan page
            setLimitError(err.message || 'Your account is in view-only mode. Reactivate your subscription to create new content.');
            navigate('/select-plan');
          } else if (!isAbortError) {
            // Only navigate away if it wasn't an abort error or limit error
            navigate('/');
          }
          // If it was aborted or limit error, stay on current page
        }
      })();

      return;
    }

    if (conversationId && !conversationId.startsWith('temp_')) {
      if (!currentConversation || currentConversation.id !== conversationId) {
        loadConversation(conversationId).catch(err => {
          console.error('Failed to load conversation:', err);
          navigate('/');
        });
      } else {
        // Load template metadata if available
        if (currentConversation.templateMetadata) {
          setTemplateContext({
            templateTitle: currentConversation.templateMetadata.templateTitle,
            helperPrompt: currentConversation.templateMetadata.helperPrompt,
            initialPrompt: currentConversation.templateMetadata.initialPrompt
          });
        }
      }
    }
  }, [conversationId, currentUser, currentConversation, loadConversation, createConversationStream, navigate, location.state, hasProcessedCreation, refreshUsage, refreshSignals, refreshReadiness, refreshSuggestions]);

  // Load template metadata when conversation is loaded
  useEffect(() => {
    if (currentConversation?.templateMetadata && currentConversation.id === conversationId) {
      setTemplateContext({
        templateTitle: currentConversation.templateMetadata.templateTitle,
        helperPrompt: currentConversation.templateMetadata.helperPrompt,
        initialPrompt: currentConversation.templateMetadata.initialPrompt
      });
    }
    // Don't automatically clear template context - only set it when metadata is present
    // This prevents losing template context during streaming updates
  }, [currentConversation?.id, currentConversation?.templateMetadata, conversationId]);

  // Scroll event listener to track if user is near the bottom
  useEffect(() => {
    const container = chatMessagesRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const nearBottom = distanceFromBottom < 150;
      isNearBottomRef.current = nearBottom;

      // Show scroll button when user scrolls up during streaming
      if (!nearBottom && streamingMessageId) {
        setShowScrollButton(true);
      } else if (nearBottom) {
        setShowScrollButton(false);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [streamingMessageId]);

  // Smart auto-scroll: only yank to bottom when not streaming or when user is near bottom
  useEffect(() => {
    if (!streamingMessageId) {
      // Not streaming — always scroll (conversation load, new message sent, etc.)
      scrollToBottom();
    } else if (isNearBottomRef.current) {
      // Streaming but user is near bottom — keep them there (instant, no smooth)
      scrollToBottomInstant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.messages, pendingUserPrompt, templateContext, showSvgCanvas]);

  // Auto-hide scroll button when streaming ends
  useEffect(() => {
    if (!streamingMessageId) {
      setShowScrollButton(false);
    }
  }, [streamingMessageId]);

  // Auto-collapse user messages with more than 4 lines
  useEffect(() => {
    if (currentConversation?.messages) {
      const newAutoCollapsed = new Set<string>();
      currentConversation.messages.forEach(msg => {
        if (msg.role === 'user' && isLongMessage(msg.content) && !autoCollapsedMessages.has(msg.id)) {
          newAutoCollapsed.add(msg.id);
        }
      });
      if (newAutoCollapsed.size > 0) {
        setAutoCollapsedMessages(prev => new Set([...Array.from(prev), ...Array.from(newAutoCollapsed)]));
        setCollapsedMessages(prev => new Set([...Array.from(prev), ...Array.from(newAutoCollapsed)]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.messages]);

  useEffect(() => {
    // Detect SVG in current conversation and build artifact history
    // Only process if the conversation ID matches the current route, or we're creating a new conversation and streaming has started
    if (currentConversation?.messages && (currentConversation.id === conversationId || (conversationId?.startsWith('temp_') && streamingMessageId !== null))) {
      const artifacts = extractSvgArtifactsFromConversation(
        currentConversation.messages,
        currentConversation.title
      );

      // Check if the streaming message contains a partial (incomplete) SVG
      const streamingMsg = currentConversation.messages.find(msg => msg.id === streamingMessageId);
      const hasCompleteSvg = artifacts.length > 0;
      // Detect an in-progress SVG by comparing opening vs closing tag counts
      const svgOpenCount = (streamingMsg?.content.match(/<svg[\s\S]/g) || []).length;
      const svgCloseCount = (streamingMsg?.content.match(/<\/svg>/gi) || []).length;
      const hasInProgressSvg = streamingMsg != null && svgOpenCount > svgCloseCount;

      // If we detect a partial SVG in streaming message, show canvas with loading state
      if (hasInProgressSvg && !hasCompleteSvg) {
        setIsSvgStreaming(true);
        setShowSvgCanvas(true);
        // Automatically collapse sidebar when SVG canvas appears
        setSidebarCollapsed(true);
        // Add class to body to hide navigation and footer
        document.body.classList.add('svg-canvas-mode');
      } else if (hasCompleteSvg && hasInProgressSvg) {
        // A completed SVG exists AND a new one is being streamed (e.g. second wireframe)
        setSvgArtifacts(artifacts);
        const latestIndex = artifacts.length - 1;
        setSelectedArtifactIndex(latestIndex);
        setCurrentSvg(artifacts[latestIndex].svgCode);
        setShowSvgCanvas(true);
        setIsSvgStreaming(true);
        setSidebarCollapsed(true);
        document.body.classList.add('svg-canvas-mode');
      } else if (hasCompleteSvg) {
        setSvgArtifacts(artifacts);
        // Always select the latest artifact by default
        const latestIndex = artifacts.length - 1;
        setSelectedArtifactIndex(latestIndex);
        setCurrentSvg(artifacts[latestIndex].svgCode);
        setShowSvgCanvas(true);
        setIsSvgStreaming(false);
        // Automatically collapse sidebar when SVG canvas appears
        setSidebarCollapsed(true);
        // Add class to body to hide navigation and footer
        document.body.classList.add('svg-canvas-mode');
      } else {
        setSvgArtifacts([]);
        setCurrentSvg(null);
        setShowSvgCanvas(false);
        setIsSvgStreaming(false);
        // Remove class from body
        document.body.classList.remove('svg-canvas-mode');
      }
    }
  }, [currentConversation?.messages, currentConversation?.title, currentConversation?.id, conversationId, streamingMessageId, setSidebarCollapsed]);

  // Cleanup: remove body class on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('svg-canvas-mode');
    };
  }, []);

  const scrollToBottom = () => {
    const container = chatMessagesRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
  };

  const scrollToBottomInstant = () => {
    const container = chatMessagesRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const handleScrollToBottom = () => {
    scrollToBottomInstant();
    isNearBottomRef.current = true;
    setShowScrollButton(false);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim() || !conversationId || sending) return;

    // Check message limit before sending
    if (!canSendMessage()) {
      setLimitError(null);
      setShowConversationLimitModal(true);
      return;
    }

    const messageToSend = message;
    const filesToSend = uploadedFiles;
    setMessage('');
    setUploadedFiles([]);
    setFileError(null);
    setLimitError(null);
    setSending(true);

    // Reset scroll state so user sees their own message + thinking indicator
    isNearBottomRef.current = true;
    setShowScrollButton(false);
    // Small delay to allow state update before scrolling
    setTimeout(() => scrollToBottomInstant(), 0);

    // Clear any existing abort controller and create new one for this request
    if (abortControllerRef.current) {
      abortControllerRef.current = null;
    }
    abortControllerRef.current = new AbortController();

    try {
      // Convert files to attachments if there are any
      let attachments = undefined;
      if (filesToSend.length > 0) {
        attachments = await filesToAttachments(filesToSend);
      }

      await sendMessageStream(conversationId, messageToSend, attachments, abortControllerRef.current.signal, handleArtifactLimitStatus);
      // Refresh usage stats after successful message
      await refreshUsage();
      // Delayed refresh: backend signal extraction and source processing are fire-and-forget
      // and call Claude, which can take varying amounts of time. Two passes to catch fast and slow extractions.
      setTimeout(() => {
        refreshSignals();
        refreshSources();
        refreshReadiness();
        refreshSuggestions();
      }, 5000);
      setTimeout(() => {
        refreshSignals();
        refreshSources();
        refreshReadiness();
        refreshSuggestions();
      }, 15000);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      console.error('Error type:', err instanceof DOMException ? 'DOMException' : err instanceof Error ? 'Error' : typeof err);
      console.error('Error name:', err?.name);
      console.error('Error code:', err?.code);

      const isAbortError = (err instanceof DOMException && err.name === 'AbortError') ||
                           (err instanceof Error && err.name === 'AbortError');

      // Check for specific limit errors and grace period errors
      const isMessageLimitError = err?.code === 'MESSAGE_LIMIT_REACHED';
      const isArtifactLimitError = err?.code === 'ARTIFACT_LIMIT_REACHED';
      const isGracePeriodError = err?.code === 'GRACE_PERIOD_ACTIVE';

      if (isAbortError) {
        // Show "Conversation stopped" message instead of error
      } else if (isMessageLimitError) {
        // Show conversation limit modal (clear any existing banner)
        setLimitError(null);
        setShowConversationLimitModal(true);
        setMessage(messageToSend);
        setUploadedFiles(filesToSend);
      } else if (isArtifactLimitError) {
        // Show artifact limit modal
        setArtifactLimitWarning('reached');
        setShowArtifactLimitModal(true);
        setMessage(messageToSend);
        setUploadedFiles(filesToSend);
      } else if (isGracePeriodError) {
        // Show grace period error - redirect to select-plan page
        setLimitError(err.message || 'Your account is in view-only mode. Reactivate your subscription to create new content.');
        navigate('/select-plan');
      } else {
        setMessage(messageToSend);
        setUploadedFiles(filesToSend);
        setFileError(err instanceof Error ? err.message : 'Failed to send message');
      }
    } finally {
      setSending(false);
      abortControllerRef.current = null;
      textareaRef.current?.focus();
    }
  };

  const handleStopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setSending(false);
      cancelStreaming(); // Clear streaming state in context
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };

  const handleSuggestedStepClick = async (text: string) => {
    if (!conversationId || sending) return;

    // Set the message and trigger send
    setMessage(text);

    // Small delay to ensure state is updated before submitting
    setTimeout(() => {
      const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSendMessage(fakeEvent);
    }, 10);
  };

  const handleModifyArtifactSubmit = async (selectedItems: string[]) => {
    if (!conversationId || sending || selectedItems.length === 0) return;

    // Format the selected items as a prompt
    const prompt = `Please make the following changes:\n${selectedItems.map(item => `- ${item}`).join('\n')}`;

    setMessage(prompt);

    // Delay to ensure state is updated before submitting
    setTimeout(() => {
      const fakeEvent = { preventDefault: () => {} } as React.FormEvent;
      handleSendMessage(fakeEvent);
    }, 100);
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      // Could add a toast notification here
    }).catch(err => {
      console.error('Failed to copy message:', err);
    });
  };

  const handleReadAloud = (content: string, messageId?: string) => {
    // Text-to-speech functionality
    if ('speechSynthesis' in window) {
      // If already playing, stop it
      if (playingMessageId) {
        window.speechSynthesis.cancel();
        setPlayingMessageId(null);
        return;
      }

      // Start new speech
      const utterance = new SpeechSynthesisUtterance(content);

      // Set playing state
      if (messageId) {
        setPlayingMessageId(messageId);
      }

      // Add listener for when speech ends
      utterance.onend = () => {
        setPlayingMessageId(null);
      };

      // Add listener for errors
      utterance.onerror = () => {
        setPlayingMessageId(null);
      };

      window.speechSynthesis.speak(utterance);
    } else {
      console.error('Text-to-speech not supported in this browser');
    }
  };

  const handleThumbsUp = async (messageId: string) => {
    if (!conversationId) return;

    // Check if message has already been rated
    if (ratedMessages.has(messageId)) {
      return;
    }

    try {
      await chatService.submitFeedback(messageId, conversationId, 'thumbs_up');
      // Mark message as rated
      setRatedMessages(prev => new Set(prev).add(messageId));
      // Show success banner
      setShowFeedbackBanner(true);
    } catch (error) {
      console.error('Failed to submit thumbs up:', error);
    }
  };

  const handleThumbsDown = async (messageId: string) => {
    if (!conversationId) return;

    // Check if message has already been rated
    if (ratedMessages.has(messageId)) {
      return;
    }

    try {
      await chatService.submitFeedback(messageId, conversationId, 'thumbs_down');
      // Mark message as rated
      setRatedMessages(prev => new Set(prev).add(messageId));
      // Show success banner
      setShowFeedbackBanner(true);
    } catch (error) {
      console.error('Failed to submit thumbs down:', error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File): string | null => {
    const allowedTypes = ['application/pdf', 'text/plain', 'image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes

    if (!allowedTypes.includes(file.type)) {
      return `Invalid file type: ${file.name}. Only PDF, TXT, PNG, JPG, and JPEG files are allowed.`;
    }

    if (file.size > maxSize) {
      return `File too large: ${file.name}. Maximum size is 10MB.`;
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFileError(null);

    // Chat attachments piggyback on the sourcesPerProject cap to keep plan limits
    // in one place. If these ever need to diverge, add a dedicated filesPerChat
    // field to PlanLimits.
    const planName = usageStats?.plan ?? 'starter';
    const maxFiles = PLAN_LIMITS[planName].sourcesPerProject ?? 5;

    // Calculate how many files we can add
    const remainingSlots = maxFiles - uploadedFiles.length;
    const filesToAdd = files.slice(0, remainingSlots);
    const rejectedCount = files.length - filesToAdd.length;

    // Validate each file that we're adding
    const validFiles: File[] = [];
    for (const file of filesToAdd) {
      const error = validateFile(file);
      if (error) {
        setFileError(error);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      validFiles.push(file);
    }

    // Add valid files
    setUploadedFiles(prev => [...prev, ...validFiles]);

    // Show error if some files were rejected due to limit
    if (rejectedCount > 0) {
      setFileError(`Maximum ${maxFiles} files allowed per chat session. ${rejectedCount} file(s) were not added.`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFileError(null);
  };

  const handleProjectChange = async (projectId: string | null) => {
    if (!conversationId || conversationId.startsWith('temp_')) return;

    try {
      if (projectId) {
        await attachConversationToProject(projectId, conversationId);
      } else {
        // Need to detach from current project if there is one
        if (currentConversation?.projectId) {
          await detachConversationFromProject(currentConversation.projectId, conversationId);
        }
      }
      // Reload the conversation to get the updated projectId
      await loadConversation(conversationId);
    } catch (error) {
      console.error('Failed to update conversation project:', error);
    }
  };

  const handleArtifactLimitStatus = (status: ArtifactLimitStatus) => {
    if (status.limitReached) {
      setArtifactLimitWarning('reached');
      setShowArtifactLimitModal(true);
    }
  };

  const formatTimestamp = (timestamp: Date | string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Only show full-page loading spinner if we're loading an existing conversation
  // Don't show it when creating a new conversation from home page
  if (loading && !currentConversation && !pendingUserPrompt && !isInitializing) {
    return (
      <Container className="d-flex justify-content-center align-items-center chat-loading-container">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  // Calculate chat container width accounting for sidebar (80px) and SVG canvas
  // When SVG canvas is visible, use calc to subtract both sidebar and canvas width
  const chatContainerStyle = showSvgCanvas
    ? { width: `calc(${100 - canvasWidth}% - 80px - ${rightSidebarOffset}px)` }
    : {};

  return (
    <>
      {showErrorBanner && error && (
        <AlertBanner
          message={error}
          type="error"
          onClose={() => setShowErrorBanner(false)}
          autoClose={true}
          autoCloseDelay={10000}
        />
      )}
      {showFeedbackBanner && (
        <AlertBanner
          message="Thank you! Your feedback has been recorded"
          type="success"
          onClose={() => setShowFeedbackBanner(false)}
          autoClose={true}
          autoCloseDelay={5000}
        />
      )}
      {limitError && (
        <AlertBanner
          message={limitError}
          type="warning"
          onClose={() => setLimitError(null)}
          autoClose={false}
        />
      )}
      <div
        className={`chat-page-container ${showSvgCanvas ? 'with-svg-canvas' : ''}`}
        style={chatContainerStyle}
      >
        <div className="chat-breadcrumbs">
          <Link to="/home" className="breadcrumb-link">Home</Link>
          <ChevronRight className="breadcrumb-separator" />
          <Link to="/chat-history" className="breadcrumb-link">Conversations</Link>
          <ChevronRight className="breadcrumb-separator" />
          <Link to="/projects" className="breadcrumb-link">Projects</Link>
          <ChevronRight className="breadcrumb-separator" />
          <span className="breadcrumb-current">
            {currentConversation?.title || 'New Conversation'}
          </span>
        </div>

      <div className="chat-messages-wrapper">
      <div className="chat-messages" ref={chatMessagesRef}>
        {templateContext && (
          <>
            <div className="message-wrapper user-message">
              <div className="message-bubble">
                <div className="message-content">
                  {templateContext.initialPrompt}
                </div>
                <div className="message-timestamp">
                  {formatTimestamp(new Date())}
                </div>
              </div>
            </div>
            <div className="message-wrapper assistant-message">
              <div className="message-bubble">
                <div className="message-content">
                  <MarkdownRenderer content={(templateContext.helperPrompt || '').replace(/\\n/g, '\n')} hideModifyArtifact={!!artifactLimitWarning} />
                </div>
                <MessageActions
                  content={templateContext.helperPrompt || ''}
                  messageId="template-helper"
                  onCopy={handleCopyMessage}
                  onReadAloud={(content) => handleReadAloud(content, "template-helper")}
                  onThumbsUp={handleThumbsUp}
                  onThumbsDown={handleThumbsDown}
                  iconSize={16}
                  isPlaying={playingMessageId === "template-helper"}
                  isRated={ratedMessages.has("template-helper")}
                />
                <div className="message-timestamp">
                  {formatTimestamp(new Date())}
                </div>
              </div>
            </div>
          </>
        )}

        {pendingUserPrompt && !currentConversation?.messages?.some(msg => msg.content === pendingUserPrompt) && (
          <div className="message-wrapper user-message">
            <div className="message-bubble">
              <div className="message-content">
                {pendingUserPrompt}
              </div>
              <div className="message-timestamp">
                {formatTimestamp(new Date())}
              </div>
            </div>
          </div>
        )}

        {currentConversation?.messages && currentConversation.messages.length > 0 && (currentConversation.id === conversationId || (conversationId?.startsWith('temp_') && streamingMessageId !== null)) ? (
          currentConversation.messages.map((msg) => {
            // Check if this message contains SVG and if SVG canvas is showing
            const hasSvg = msg.role === 'assistant' && extractSvgFromText(msg.content);
            const shouldHideSvg = showSvgCanvas && hasSvg;
            const isStreamingSvg = isSvgStreaming && streamingMessageId === msg.id;

            let displayContent: string;
            if (shouldHideSvg && isStreamingSvg) {
              // Complete SVG(s) exist AND a new one is being streamed
              // First replace complete SVGs, then strip the trailing partial SVG
              let cleaned = replaceSvgWithArtifactMessage(msg.content);
              // Find the last incomplete <svg tag (no matching </svg>)
              const lastSvgOpen = cleaned.lastIndexOf('<svg');
              if (lastSvgOpen > -1) {
                let trimIndex = lastSvgOpen;
                const preceding = cleaned.substring(0, lastSvgOpen);
                const fenceMatch = preceding.match(/```(?:svg|xml)?\s*\n?\s*$/);
                if (fenceMatch && fenceMatch.index !== undefined) {
                  trimIndex = fenceMatch.index;
                }
                cleaned = cleaned.substring(0, trimIndex).trimEnd();
                cleaned = cleaned.replace(/\*\*Artifact:\s*.+?\*\*\s*$/, '').trimEnd();
              }
              displayContent = cleaned;
            } else if (shouldHideSvg) {
              displayContent = replaceSvgWithArtifactMessage(msg.content);
            } else if (isStreamingSvg) {
              // Strip partial SVG markup (and preceding code fence) from display during streaming
              const svgStartIndex = msg.content.indexOf('<svg');
              if (svgStartIndex > -1) {
                let trimIndex = svgStartIndex;
                // Also strip the opening code fence (e.g. ```svg\n) before the <svg tag
                const preceding = msg.content.substring(0, svgStartIndex);
                const fenceMatch = preceding.match(/```(?:svg|xml)?\s*\n?\s*$/);
                if (fenceMatch && fenceMatch.index !== undefined) {
                  trimIndex = fenceMatch.index;
                }
                let cleaned = msg.content.substring(0, trimIndex).trimEnd();
                // Also strip trailing **Artifact: <title>** line if present
                cleaned = cleaned.replace(/\*\*Artifact:\s*.+?\*\*\s*$/, '').trimEnd();
                displayContent = cleaned;
              } else {
                displayContent = msg.content;
              }
            } else {
              displayContent = msg.content;
            }

            return (
              <div
                key={msg.id}
                className={`message-wrapper ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
              >
                <div className="message-bubble">
                  <div className="message-content">
                    {msg.role === 'assistant' ? (
                      <>
                        <MarkdownRenderer
                          content={displayContent}
                          isStreaming={streamingMessageId === msg.id}
                          onSuggestedStepClick={handleSuggestedStepClick}
                          onModifyArtifactSubmit={handleModifyArtifactSubmit}
                          hideModifyArtifact={!!artifactLimitWarning}
                        />
                        {isStreamingSvg && (
                          <div className="generating-indicator">
                            <div className="generating-bar">
                              <div className="generating-bar-fill"></div>
                            </div>
                            <span className="generating-label">Generating wireframe...</span>
                          </div>
                        )}
                        {msg.truncated && streamingMessageId !== msg.id && (
                          <div className="truncation-notice" role="status">
                            Response was cut off (output limit reached). Ask Claude to continue, or try simplifying the request.
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {isLongMessage(msg.content) && (
                          <button
                            className="user-message-collapse-btn"
                            onClick={() => setCollapsedMessages(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(msg.id)) {
                                newSet.delete(msg.id);
                              } else {
                                newSet.add(msg.id);
                              }
                              return newSet;
                            })}
                            title={collapsedMessages.has(msg.id) ? "Expand message" : "Collapse message"}
                          >
                            {collapsedMessages.has(msg.id) ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                          </button>
                        )}
                        {collapsedMessages.has(msg.id) ? (
                          <span className="truncated-message-text">{getTruncatedContent(msg.content)}</span>
                        ) : (
                          msg.content
                        )}
                      </>
                    )}
                  </div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <AttachmentDisplay attachments={msg.attachments} />
                  )}
                  {msg.role === 'assistant' && streamingMessageId !== msg.id && (
                    <MessageActions
                      content={msg.content}
                      messageId={msg.id}
                      onCopy={handleCopyMessage}
                      onReadAloud={(content) => handleReadAloud(content, msg.id)}
                      onThumbsUp={handleThumbsUp}
                      onThumbsDown={handleThumbsDown}
                      iconSize={16}
                      isPlaying={playingMessageId === msg.id}
                      isRated={ratedMessages.has(msg.id)}
                    />
                  )}
                  <div className="message-timestamp">
                    {formatTimestamp(msg.timestamp)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          !loading && (
            <div className="text-center text-muted p-4">
              No messages yet. Start the conversation!
            </div>
          )
        )}

        {(sending || (isInitializing && !currentConversation?.messages?.length)) && (
          <div className="message-wrapper assistant-message">
            <div className="message-bubble">
              <div className="thinking-indicator">
                <div className="thinking-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="thinking-label">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {showScrollButton && (
        <button className="scroll-to-bottom-btn" onClick={handleScrollToBottom}>
          <ChevronDown size={16} />
          <span>New messages</span>
        </button>
      )}
      </div>

      <div className="chat-input-container">
        <Form onSubmit={handleSendMessage} className="chat-form">
          <div className="d-flex flex-column">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.txt,.png,.jpg,.jpeg"
              multiple
              className="chat-file-input-hidden"
            />

            {uploadedFiles.length > 0 && (
              <div className="files-container mb-3">
                {uploadedFiles.map((file, index) => (
                  <FileCard
                    key={`${file.name}-${index}`}
                    file={file}
                    onRemove={() => handleRemoveFile(index)}
                  />
                ))}
              </div>
            )}

            {fileError && (
              <div className="file-error-box">
                <InfoCircle size={16} className="file-error-icon" />
                <span className="file-error-text">{fileError}</span>
              </div>
            )}

            <div className="chat-textarea-wrapper mb-3">
              <Dropdown className="project-dropdown">
                <Dropdown.Toggle variant="outline-secondary" size="sm" id="chat-project-dropdown">
                  <span className="me-2">
                    {currentConversation?.projectId
                      ? projects.find(p => p.id === currentConversation.projectId)?.name || 'NO PROJECT SELECTED'
                      : 'NO PROJECT SELECTED'
                    }
                  </span>
                  <ChevronDown />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleProjectChange(null)}>
                    NO PROJECT SELECTED
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item
                    onClick={() => openNewProjectDialog({ onLimitError: () => setShowProjectLimitModal(true) })}
                    className="text-primary"
                  >
                    <PlusCircle className="me-2" />
                    CREATE NEW PROJECT
                  </Dropdown.Item>
                  {projects.length > 0 && <Dropdown.Divider />}
                  {projects.map(project => (
                    <Dropdown.Item
                      key={project.id}
                      onClick={() => handleProjectChange(project.id)}
                      active={currentConversation?.projectId === project.id}
                    >
                      {project.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Form.Control
                as="textarea"
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="ANYTHING YOU'D LIKE TO CHANGE?"
                disabled={sending}
                rows={1}
                className="chat-textarea chat-textarea-inline"
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              {!streamingMessageId && !sending && (
                <Button
                  type="button"
                  variant="link"
                  size="lg"
                  onClick={handleUploadClick}
                  disabled={uploadedFiles.length >= 5}
                >
                  <Upload className="me-2" />
                  Upload
                </Button>
              )}
              <Button
                type={streamingMessageId ? "button" : "submit"}
                variant="primary"
                size="lg"
                disabled={!streamingMessageId && (!message.trim() || sending)}
                onClick={streamingMessageId ? handleStopStreaming : undefined}
              >
                {streamingMessageId ? (
                  <>
                    <StopFill className="me-2 stop-icon" />
                    Stop
                  </>
                ) : (
                  <>
                    <Stars className="me-2" />
                    {showSvgCanvas ? 'Ask' : 'Ask Revealer'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Form>
      </div>
      {!showSvgCanvas && <Footer />}
    </div>
    {showSvgCanvas && (
      <SvgCanvas
        svgCode={currentSvg || ''}
        width={canvasWidth}
        onWidthChange={setCanvasWidth}
        rightOffset={rightSidebarOffset}
        title={currentConversation?.title}
        templateName={templateContext?.templateTitle}
        artifacts={svgArtifacts}
        selectedArtifactIndex={selectedArtifactIndex}
        onArtifactSelect={(index) => {
          setSelectedArtifactIndex(index);
          setCurrentSvg(svgArtifacts[index].svgCode);
        }}
        onClose={() => {
          setShowSvgCanvas(false);
          setCurrentSvg(null);
          setCanvasWidth(45); // Reset to default width
        }}
        isStreaming={isSvgStreaming}
      />
    )}
    <ProjectLimitModal
      show={showProjectLimitModal}
      onHide={() => setShowProjectLimitModal(false)}
      planName={usageStats?.plan || 'starter'}
    />
    <ConversationLimitModal
      show={showConversationLimitModal}
      onHide={() => setShowConversationLimitModal(false)}
      planName={usageStats?.plan || 'starter'}
    />
    <ArtifactLimitModal
      show={showArtifactLimitModal}
      onHide={() => setShowArtifactLimitModal(false)}
      planName={usageStats?.plan || 'starter'}
    />
    </>
  );
};

export default Chat;